import { useState } from 'react';
import { Card } from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Checkbox } from "@shared/components/ui/checkbox";
import { Badge } from "@shared/components/ui/badge";
import { Home, Calendar, Banknote, Loader2, Clock, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { format, addMonths } from 'date-fns';
import { ar } from 'date-fns/locale';
import { getPhotoUrl } from '@/types';

export default function RentalAgreement({ property, userData, onComplete }) {
  const [agreed, setAgreed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const monthlyRent = 10000;
  const rentalPeriod = 2;
  const startDate = new Date();
  const endDate = addMonths(startDate, rentalPeriod);

  const formatDate = (date) => {
    return format(date, 'dd MMMM yyyy', { locale: ar });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const handleSign = () => {
    setIsLoading(true);
    setTimeout(() => {
      onComplete();
    }, 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen p-4 md:p-8 bg-gradient-to-br from-slate-50 to-slate-100"
    >
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg p-2">
            <img src="/images/logos/ejar.png" alt="Ejar" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">عقد الإيجار المؤقت</h1>
          <p className="text-slate-500">اسكن في منزلك الجديد أثناء انتظار موافقة البنك</p>
        </div>

        <Card className="p-6 md:p-8 border-0 shadow-xl mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Badge className="bg-blue-100 text-blue-700 border-0 px-3 py-1">
              <Clock className="w-4 h-4 ml-1" />
              عقد مؤقت
            </Badge>
            <Badge className="bg-emerald-100 text-emerald-700 border-0 px-3 py-1">
              <CheckCircle2 className="w-4 h-4 ml-1" />
              معتمد من إيجار
            </Badge>
          </div>

          {/* Property Info */}
          <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl mb-6">
            <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
              <img
                src={getPhotoUrl(property?.photos, 'thumb')}
                alt={property?.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-1">{property?.title}</h3>
              <p className="text-sm text-slate-500">
                {property?.district?.label ? `${property.district.label}، ` : ''}
                {property?.city?.label}
              </p>
            </div>
          </div>

          {/* Agreement Details */}
          <div className="space-y-4 mb-8">
            <h3 className="font-bold text-lg text-slate-900">تفاصيل العقد</h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 text-slate-500 mb-2">
                  <Home className="w-4 h-4" />
                  <span className="text-sm">المستأجر</span>
                </div>
                <p className="font-semibold text-slate-900">{userData.name}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 text-slate-500 mb-2">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">مدة العقد</span>
                </div>
                <p className="font-semibold text-slate-900">{rentalPeriod} شهر</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 text-slate-500 mb-2">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">تاريخ البدء</span>
                </div>
                <p className="font-semibold text-slate-900">{formatDate(startDate)}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 text-slate-500 mb-2">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">تاريخ الانتهاء</span>
                </div>
                <p className="font-semibold text-slate-900">{formatDate(endDate)}</p>
              </div>
            </div>

            <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
              <div className="flex items-center gap-2 text-blue-700 mb-2">
                <Banknote className="w-5 h-5" />
                <span>الإيجار الشهري</span>
              </div>
              <p className="text-3xl font-bold text-blue-700">{formatCurrency(monthlyRent)}</p>
              <p className="text-sm text-blue-600 mt-2">
                إجمالي الإيجار: {formatCurrency(monthlyRent * rentalPeriod)}
              </p>
            </div>

            <div className="bg-emerald-50 rounded-xl p-4">
              <p className="text-sm text-emerald-800">
                <strong>ملاحظة:</strong> هذا عقد إيجار مؤقت يتيح لك السكن في العقار أثناء انتظار الموافقة النهائية على التمويل العقاري. سيتم خصم مبلغ الإيجار من الدفعة الأولى عند اكتمال عملية الشراء.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 mb-6">
            <Checkbox
              id="agree"
              checked={agreed}
              onCheckedChange={setAgreed}
              className="mt-1"
            />
            <label htmlFor="agree" className="text-sm text-slate-600 cursor-pointer">
              أقر بأنني قرأت شروط عقد الإيجار المؤقت وأوافق على الالتزام بسداد الإيجار الشهري المحدد خلال فترة العقد.
            </label>
          </div>

          <Button
            onClick={handleSign}
            disabled={!agreed || isLoading}
            className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-lg font-medium disabled:opacity-50"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              'اصدار عقد الايجار المؤقت'
            )}
          </Button>
        </Card>
      </div>
    </motion.div>
  );
}